import { ResourceRequestParams, ResourceResponse, getResource } from "@/api/resource";
import { usePageState, useResourceData } from "@/context/PageStateContext";
import { getCommonParams } from "@/lib/commonParams";
import { Level, reportCustomMetric, reportError, reportPerfMetric } from "@/lib/owl";
import { FloatButton, HeadBanner, ShowCaseInfo } from "@/types/resource-slot";
import { useCallback, useEffect, useState } from "react";

function reportGetResourceDataSuccessRate(data: ResourceResponse) {
  try {
    // 获取头图成功率
    reportCustomMetric({
      key: 'getResourceData',
      value: data?.headBanner?.imageUrl ? 1 : 0,
      tags: {
        resourceType: 'headBanner',
      },
    })
    // 获取底部浮层文案成功率
    reportCustomMetric({
      key: 'getResourceData',
      value: data?.floatButton?.name ? 1 : 0,
      tags: {
        resourceType: 'floatButton',
      },
    })
    // 获取资源位成功率
    reportCustomMetric({
      key: 'getResourceData',
      value: data?.showCaseInfo?.showCaseList?.length ? 1 : 0,
      tags: {
        resourceType: 'showCase',
      },
    })
  } catch (err) {
    reportError({
      name: '[useResource] reportGetResourceDataSuccessRateError',
      msg: err,
      category: 'jsError',
      level: Level.ERROR,
    })
  }
}

export default function useResource({
  resourceScene,
}: {
  resourceScene: number;
}) {
  const { locationInfo, isOpenCity, setModuleLoaded } = usePageState();
  const { resourceData } = useResourceData();

  // 服务端渲染时，如果Context中有数据，直接初始化state
  const [headBanner, setHeadBanner] = useState<HeadBanner | null>(() => {
    if (resourceData) {
      return resourceData.headBanner;
    }
    return null;
  });
  const [floatButton, setFloatButton] = useState<FloatButton | null>(() => {
    if (resourceData) {
      return resourceData.floatButton;
    }
    return null;
  });
  const [showCaseInfo, setShowCaseInfo] = useState<ShowCaseInfo | null>(() => {
    if (resourceData) {
      return resourceData.showCaseInfo;
    }
    return null;
  });
  const [isInitFinished, setIsInitFinished] = useState<boolean>(() => {
    // 🆕 如果Context中有数据，标记为已初始化
    return !!resourceData;
  });

  const getResourceData = useCallback(async () => {
    // 上报请求资源位数据开始时间
    const getResourceDataStartTime = Date.now();
    if (!isInitFinished && (window as any)?.__PAGE_JS_START__) {
      reportPerfMetric({
        key: "getResourceDataStart",
        value: getResourceDataStartTime - (window as any)?.__PAGE_JS_START__,
      })
    }
    try {
      const commonParams = await getCommonParams();
      const params = {
        ...commonParams,
        resourceScene,
      } as ResourceRequestParams;
      const { data: result } = await getResource(params);
      setHeadBanner(result?.headBanner || null);
      setFloatButton(result?.floatButton || null);
      setShowCaseInfo(result?.showCaseInfo || null);
      // 上报获取资源位数据成功率
      reportGetResourceDataSuccessRate(result);
    } catch (error) {
      reportError({
        name: '[useResource] getResourceDataError',
        msg: error,
        category: 'jsError',
        level: Level.ERROR,
      })
      console.error('[useResource] 获取资源位数据失败:', error);
    } finally {
      if (!isInitFinished) {
        const getResourceDataEndTime = Date.now();
        setIsInitFinished(true);
        setModuleLoaded('resource');

        // 上报请求资源位数据耗时
        reportPerfMetric({
          key: "getResourceDataCost",
          value: getResourceDataEndTime - getResourceDataStartTime,
        })
        // 上报请求资源位数据结束时间
        if ((window as any)?.__PAGE_JS_START__) {
          reportPerfMetric({
            key: "getResourceDataEnd",
            value: getResourceDataEndTime - (window as any)?.__PAGE_JS_START__,
          })
        }
      }
    }
  }, [resourceScene, locationInfo, isInitFinished, setModuleLoaded]);

  // cityId 变化时，重新获取资源位数据
  useEffect(() => {
    // 等待定位初始化
    if (!locationInfo?.isInited) {
      return;
    }
    // 页面未对当前城市开放，不发起接口请求
    if (!isOpenCity) {
      return;
    }
    // 资源位接口只和 cityId 相关，如果已经有预取数据，判断 cityId 相同，不刷新数据
    if (isInitFinished && locationInfo?.ssrDiff?.isSameSelectCityId) {
      setModuleLoaded('resource');
      return;
    }
    getResourceData();
  }, [locationInfo]);

  return {
    isInitFinished,
    headBanner,
    floatButton,
    showCaseInfo,
  }
}